import UniversalPageLayout from "../components/BaseComponents/UniversalPageLayout/UniversalPageLayout";
import routes from "../components/Router/routes";
import useUserData from "../hooks/useUserData";
import { useContext, useEffect, useState } from "react";
import { Button, InlineAlert, LoadingSpinner } from "../components/BaseComponents";
import CanonicalModal from "../components/BaseComponents/CanonicalModal/CanonicalModal";
import AccordionSection from "../components/BaseComponents/AccordionSection";
import { FICTITIOUS_HINTS, SUFFIX_CLASS } from "../components/OrganisationSwitcher/OrganisationSwitcher";
import UserSettingsSection from "../components/UserSettingsSection/UserSettingsSection";
import ConnectOrganisationSetting from "../components/ConnectOrganisationSetting/ConnectOrganisationSetting";
import { SettingsGroup, SettingsItem, SettingsSubItem } from "../components/SettingsLayout/SettingsLayout";
import { isSampleState } from "../lib/sampleState";
import { getApiErrorMessage, getCSRFToken, getCurrencySymbol } from "../lib/utils";
import SimpleSortTable from "../components/BaseComponents/SimpleSortTable/SimpleSortTable";
import useApi from "../hooks/useApi";
import useCleanup from "../hooks/useCleanup";
import { userContext } from "../context/UserContext";
import useIsViewOnly from "../hooks/useIsViewOnly";

function CompanyUsers() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<any[]>([]);

    const api = useApi();

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            const result = await api.get('company/users')
            if(cancelled) {
                return;
            }
            setData(result.data.users);
            setLoading(false);
        };
        run();
        return () => {
            cancelled = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const columns = [
        {
            id: 'email',
            // sortable: true,
            label: 'Email',
        },
        {
            id: 'fullName',
            // sortable: true,
            label: 'Name',
        },
        {
            id: 'role',
            // sortable: true,
            label: 'Role',
        },
    ]

    // An empty group still shows its table, with "None" in the first row.
    const rowsOrNone = (users: any[]) => users.length > 0 ? users : [{ _id: 'none', email: 'None' }];

    return (
        <SettingsItem title="Users">
            {loading ? <LoadingSpinner /> : (
                <div className="space-y-6">
                    <SettingsSubItem title="Active">
                        <SimpleSortTable
                            data={rowsOrNone(data.filter(user => !user.locked))}
                            columns={columns}
                        />
                    </SettingsSubItem>
                    <SettingsSubItem title="Locked">
                        <SimpleSortTable
                            data={rowsOrNone(data.filter(user => user.locked))}
                            columns={columns}
                        />
                    </SettingsSubItem>
                </div>
            )}
        </SettingsItem>
    )

}

function AdminEntryPermission() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<any>(null);
    const api = useApi();
    const assumedCompanyType = useUserData().access?.assumedCompanyType;
    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            const result = await api.get('/companySettings')
            if(cancelled) {
                return;
            }

            setData(result.data);
            setLoading(false);
        }

        run();

        return () => {
            cancelled = true;
        }
    }, []);

    const onChange = async (enabled: boolean) => {
        setLoading(true);
        const result = await api.put('/companySettings', {
            _csrf: await getCSRFToken(),
            settings: {
                adminEntryPermission: enabled,
            }
        })

        setData(result.data)
        setLoading(false);
    }

    const enabled = data?.adminEntryPermission;

    const warning = "You have enabled access for the Neoni Support Team, who can currently see all your organisation's data.";

    if (assumedCompanyType) {
        return (
            <SettingsItem
                title="Permit Neoni Support Team access"
                description={loading || enabled ? undefined : 'Neoni Support Team access is not enabled.'}
            >
                {loading ? <LoadingSpinner /> : (
                    enabled && (
                        <InlineAlert type="error" className="" data-testid="neoni-access-warning">
                            {warning} Only members of this organisation can change this setting.
                        </InlineAlert>
                    )
                )}
            </SettingsItem>
        )
    }
    return (
        <SettingsItem title="Permit Neoni Support Team access">
            {loading ? <LoadingSpinner /> : (
                enabled ? (
                    <>
                        <InlineAlert type="error" className="" data-testid="neoni-access-warning">
                            {warning} You can disable this access below.
                        </InlineAlert>
                        <div>
                            <Button requiresWrite type="Outlined" size="sm" text="Disable" onClick={() => onChange(false)} />
                        </div>
                    </>
                ) : (
                    <>
                        <div>
                            <Button requiresWrite type="Outlined" size="sm" text="Enable" onClick={() => onChange(true)} />
                        </div>
                        <div className="text-error-400 text-sm" data-testid="neoni-access-danger-zone">
                            DANGER ZONE: This will enable the Neoni Support Team to view and access all your data. Only proceed if you are comfortable with this.
                        </div>
                    </>
                )
            )}
        </SettingsItem>
    )

}

function ResetSampleOrganisation() {
    const userData = useUserData();
    const { fetchUserData } = useContext(userContext);
    const api = useApi();
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    if (!isSampleState(userData) || userData.access?.assumedCompanyType) {
        return null;
    }

    const onConfirm = async () => {
        setBusy(true);
        setError('');
        try {
            await api.post('/sample-organisation/reset', { _csrf: await getCSRFToken() });
            await fetchUserData?.();
            setOpen(false);
        } catch (err) {
            setError(getApiErrorMessage(err, 'Could not reset your sample organisation. Please try again.'));
        } finally {
            setBusy(false);
        }
    }

    return (
        <SettingsItem title="Sample organisation">
            <div>
                <Button
                    type="Outlined"
                    size="sm"
                    text="Reset sample organisation"
                    onClick={() => setOpen(true)}
                />
            </div>
            <div className="text-charcoal-100 text-sm">Restore your sample organisation to its original data. Any changes you made to it are lost.</div>
            <CanonicalModal
                open={open}
                onClose={() => setOpen(false)}
                title="Reset sample organisation"
                subtitle={userData.companyName}
                actions={
                    <>
                        <Button
                            type="Primary"
                            size="sm"
                            text={busy ? 'Resetting...' : 'Reset'}
                            disabled={busy}
                            onClick={onConfirm}
                            additionalClass="mr-5"
                        />
                        <Button type="Outlined" size="sm" text="Cancel" onClick={() => setOpen(false)} />
                    </>
                }
            >
                <p className="text-white mt-8">
                    This deletes your sample organisation and its data and replaces it with a fresh copy. Other users are not affected.
                </p>
                {error && <InlineAlert type="error" className="mt-4">{error}</InlineAlert>}
            </CanonicalModal>
        </SettingsItem>
    )
}

function CompanySettings() {
    const userData = useUserData();
    const symbol = userData.currencyCode ? getCurrencySymbol(userData.currencyCode) : undefined;
    const currencyLabel = symbol ? `${userData.currencyCode} (${symbol})` : userData.currencyCode;
    const fictitiousKind = userData.access?.fictitiousKind;
    const organisationTitle = userData.companyName ? (
        <>
            Organisation: {userData.companyName}
            {fictitiousKind && <span className={SUFFIX_CLASS}> ({FICTITIOUS_HINTS[fictitiousKind]})</span>}
        </>
    ) : 'Organisation';
    return (
		<UniversalPageLayout
			documentTitle={routes.companySettings.name}
			heading={{ title: 'Account Settings' }}
		>
			<div className="text-white">
				<UserSettingsSection />
				<AccordionSection
					id="settings-organisation"
					title={organisationTitle}
					defaultOpen
				>
					<SettingsGroup>
						<ConnectOrganisationSetting />
						<SettingsItem title="Currency">
							<div className="text-white">{currencyLabel}</div>
						</SettingsItem>
						<ResetSampleOrganisation />
						<CompanyUsers />
						<AdminEntryPermission />
					</SettingsGroup>
				</AccordionSection>
			</div>
		</UniversalPageLayout>
    )
}

export default CompanySettings;
